/** The pane beside the queue: what a reviewer is attesting, then the decision.
 *
 * **It is read only when a row is chosen.** The queue is one server-filtered request and a row costs
 * nothing (the N+1 the queue exists to refuse); choosing a row is the reviewer saying "this one", and
 * only then are its figure, its receipt and its workflow read. Nothing here is fetched on load.
 *
 * **The thing attested is the receipt, not a delta.** For a figure it is the six-link chain behind
 * it (`ReceiptAttest`); for a filing it is the report as it stands, its verdict in its own words and
 * the way into the checks. A kind this pane has no composition for says so and links to the record,
 * where its facts are drawn: it does not invent a summary. The decision itself is the record page's
 * own panel (`ApprovalPanel`), unchanged - one place decides, so the six server-side refusals
 * (named reviewer, stage order, separation, an open blocking flag, replay, a route with no stages)
 * cannot be skipped by a second form that forgot one.
 */

import { Link } from "@tanstack/react-router";

import type { AwaitingRequest } from "../api/approvals";
import { useMe } from "../api/auth";
import { useActivity, useReceipt, useReport } from "../api/queries";
import type { Scope2Method } from "../api/queries";
import { useRecordWork } from "../api/work";
import { ReadFailed } from "../components/Loaded";
import { Empty } from "../components/Panel";
import { ReceiptAttest } from "../components/ReceiptAttest";
import { SkeletonRows } from "../components/ui/Skeleton";
import { StatusChip } from "../components/ui/StatusChip";
import { quantity } from "../lib/format";
import { kindLabel } from "./kinds";
import { FiguresInRegister } from "./ReportsLines";
import { Approval as ApprovalPanel } from "./RecordWork";
import styles from "./ApprovalsAttest.module.css";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import { VERDICT_HEADLINE } from "./verdictWords";

function FigureAttest({ recordId, onOpenReceipt }: { recordId: string; onOpenReceipt: (id: string) => void }) {
  const activity = useActivity(recordId);
  const calculation = activity.data?.calculations[0];
  const receipt = useReceipt(calculation?.calculation_id);
  if (activity.error) {
    return <ReadFailed of="This record" error={activity.error} onRetry={() => void activity.refetch()} />;
  }
  if (activity.isPending || (calculation && receipt.isPending)) {
    return (
      <div role="status" aria-busy="true">
        <span className="visuallyHidden">Reading the figure and its receipt…</span>
        <SkeletonRows rows={4} />
      </div>
    );
  }
  if (!activity.data || !calculation) {
    return <Empty>This record has no calculated figure yet, so there is no receipt to attest.</Empty>;
  }
  if (receipt.error || !receipt.data) {
    return <ReadFailed of="The receipt" error={receipt.error} onRetry={() => void receipt.refetch()} />;
  }
  return (
    <>
      <ReceiptAttest
        receipt={receipt.data}
        onOpenReceipt={() => onOpenReceipt(calculation.calculation_id)}
      />
      {activity.data.calculations.length > 1 ? (
        <p className={styles.note}>
          This record has {activity.data.calculations.length} figures; the first is shown. The others
          are on the record.
        </p>
      ) : null}
    </>
  );
}

function FilingAttest({ obligationId, method }: { obligationId: string; method: Scope2Method }) {
  const report = useReport(obligationId, method);
  if (report.error) {
    return <ReadFailed of="This filing" error={report.error} onRetry={() => void report.refetch()} />;
  }
  if (!report.data) {
    return (
      <div role="status" aria-busy="true">
        <span className="visuallyHidden">Reading the filing…</span>
        <SkeletonRows rows={3} />
      </div>
    );
  }
  const filing = report.data;
  const blocking = filing.findings.filter((finding) => finding.severity === "blocking").length;
  return (
    <div className={styles.filing} data-testid="attest-filing">
      <p className={styles.verdict}>
        <StatusChip tone={blocking > 0 ? "crit" : filing.verdict === "approved" || filing.verdict === "issued" ? "ok" : "info"}>
          {VERDICT_HEADLINE[filing.verdict] ?? filing.verdict}
        </StatusChip>
      </p>
      <p className={styles.stated}>
        <span className={`${styles.statedValue} display`}>{quantity(filing.stated_total_tonnes, 1)}</span>
        <span className={styles.unit}>tCO₂e stated under the regulation</span>
      </p>
      <p className={styles.note}>
        {blocking} blocking, {filing.findings.length - blocking} advisory · {filing.rules_evaluated}{" "}
        check{filing.rules_evaluated === 1 ? "" : "s"} run. {filing.verdict_reason}
      </p>
      <p className={styles.links}>
        <FiguresInRegister report={filing} testId="attest-figures">
          Show the figures behind it
        </FiguresInRegister>
      </p>
    </div>
  );
}

export function AttestPane({
  request,
  method,
  onOpenReceipt,
  onClose,
}: {
  request: AwaitingRequest;
  method: Scope2Method;
  onOpenReceipt: (calculationId: string) => void;
  onClose: () => void;
}) {
  const { data: me } = useMe();
  const work = useRecordWork(request.record_id);
  const kind = kindLabel(request.record_kind);

  return (
    <aside className={styles.pane} aria-label={`Attest the ${kind.toLowerCase()}`} data-testid="attest-pane">
      <header className={styles.head}>
        <div>
          <h3 className={styles.title}>{kind}</h3>
          <p className={styles.meta}>
            {request.route_name} · {request.stage_name}
          </p>
        </div>
        <button type="button" className={styles.close} onClick={onClose} data-testid="attest-close">
          Close
        </button>
      </header>

      <section className={styles.section} aria-label="What you are attesting">
        <h4 className={styles.sectionHead}>What you are attesting</h4>
        {request.record_kind === "activity_record" ? (
          <FigureAttest recordId={request.record_id} onOpenReceipt={onOpenReceipt} />
        ) : request.record_kind === "filing_obligation" ? (
          <FilingAttest obligationId={request.record_id} method={method} />
        ) : (
          <p className={styles.note} data-testid="attest-other">
            This kind of record draws its own facts on its page.
          </p>
        )}
        <p className={styles.links}>
          {request.record_kind === "filing_obligation" ? (
            <Link
              to="/reports"
              search={(prev: Partial<ScreenSearch>) => ({
                ...keepSearch(prev),
                filing: request.record_id,
                tab: "checks",
              })}
              className={styles.link}
              data-testid="attest-open-filing"
            >
              Open the filing and its checks
            </Link>
          ) : null}
          <Link
            to="/records/$recordId"
            params={{ recordId: request.record_id }}
            search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "workflow" })}
            className={styles.link}
          >
            Open the record
          </Link>
        </p>
      </section>

      <section className={styles.section} aria-label="Your decision">
        <h4 className={styles.sectionHead}>Your decision</h4>
        {work.error ? (
          <ReadFailed of="This request" error={work.error} onRetry={() => void work.refetch()} />
        ) : !work.data ? (
          <div role="status" aria-busy="true">
            <span className="visuallyHidden">Reading the request…</span>
            <SkeletonRows rows={2} />
          </div>
        ) : (
          <ApprovalPanel
            recordId={request.record_id}
            kind={request.record_kind}
            revision={request.record_version}
            work={work.data}
            me={me ?? null}
            reload={() => void work.refetch()}
          />
        )}
      </section>
    </aside>
  );
}
